<script lang="ts">
  import { browser } from '$app/environment';
  import CornerIcon from '$icons/CornerIcon.svelte';
  import FolderGit2Icon from '$icons/FolderGit2Icon.svelte';
  import XLogo from '$icons/logos/XLogo.svelte';
  import PinIcon from '$icons/PinIcon.svelte';
  import PresentationIcon from '$icons/PresentationIcon.svelte';
  import Button from '$lib/components/reusable/Button.svelte';
  import ExpandableText from '$lib/components/reusable/ExpandableText.svelte';
  import ImageRender from '$lib/components/reusable/images/ImageRender.svelte';
  import SectionTitle from '$lib/components/reusable/SectionTitle.svelte';
  import Tag from '$lib/components/reusable/Tag.svelte';
  import Tooltip from '$lib/components/reusable/Tooltip.svelte';
  import { type HackathonProject } from '$shared/lib';
  import BookIcon from '$icons/BookIcon.svelte';
  import UserGroupIcon from '$icons/users/UserGroupIcon.svelte';
  import LinkTwoIcon from '$icons/links/LinkTwoIcon.svelte';
  import LongPaperIcon from '$icons/LongPaperIcon.svelte';
  import type { User } from 'src/routes/(app)/+layout.server';
  import { cn } from '$lib/utils/misc';
  import Notification from '$lib/components/reusable/Notification.svelte';
  import LeaveTeamModal from '../../../pages/Projects/LeaveTeamModal.svelte';
  import DeleteProjectModal from '../../../pages/Projects/DeleteProjectModal.svelte';
  import HackathonProjectActions from '../../../pages/Projects/HackathonProjectActions.svelte';
  import HackathonProjectMembers from '../../../pages/Projects/HackathonProjectMembers.svelte';
  import HackathonProjectComments from '$lib/pages/Projects/HackathonProjectComments.svelte';
  import DoubleBubbleIcon from '$icons/messages/DoubleBubbleIcon.svelte';
  import Likes from './likes/LikeButton.svelte';
  import { HackathonPrizeType, type ProjectMember, type HackathonWithPhase } from '$shared/lib';
  import PrizeBadge from '../PrizeBadge.svelte';
  import { linkify } from '$utils';
  import BuildIcon from '$icons/BuildIcon.svelte';

  export let project: HackathonProject<ProjectMember>;
  export let hackathon: HackathonWithPhase | null = null;
  export let user: User | null = null;
  export let showLinkConfirmation = false;

  let showLeaveTeamModal = false;
  let showDeleteProjectModal = false;

  $: isProjectFromCurrentHackathon = hackathon && project.hackathonId === hackathon.id;
  $: prizeStatus = project.prize
    ? project.prize.type === HackathonPrizeType.GrandPrize
      ? 'grand'
      : project.prize.type === HackathonPrizeType.HonorableMention
        ? 'honorable'
        : 'prize'
    : 'none';
</script>

<div
  class={cn(
    'rounded-2xs md:border-gray-dark-6 md:p-3xs relative w-full overflow-hidden border border-transparent p-0',
    $$props.class,
  )}
>
  {#if prizeStatus !== 'none'}
    <div
      class={cn('shine', {
        'shine-gold': prizeStatus === 'grand',
        'shine-mint': prizeStatus === 'prize',
        'shine-silver': prizeStatus === 'honorable',
      })}
    />
  {/if}
  <div
    class="rounded-2xs md:border-gray-dark-2 md:p-md bHackathonProjectorder-transparent flex w-full flex-col"
  >
    <div
      class={cn(
        'gap-x-lg rounded-3xs border-gray-dark-1 relative flex w-full flex-col gap-y-3 border p-3',
        prizeStatus === 'grand' &&
          'border-amber-400/20 bg-gradient-to-b from-amber-900/30 to-transparent',
        prizeStatus === 'prize' &&
          'border-mint-dark-11/10 from-mint-dark-11/5 bg-gradient-to-b to-transparent',
        prizeStatus === 'honorable' &&
          'border-gray-dark-11/20 from-gray-dark-11/5 bg-gradient-to-b to-transparent',
      )}
    >
      {#if !isProjectFromCurrentHackathon || hackathon?.isProjectDirectoryEnabled}
        <Likes class="absolute right-4" {project} {user} />
      {/if}
      {#if project.prize}
        <PrizeBadge size="sm" class="self-start" prize={project.prize} />
      {/if}
      <CornerIcon
        class={cn(
          'absolute right-0 top-0 -m-0.5',
          prizeStatus === 'grand' && 'text-amber-400',
          prizeStatus === 'prize' && 'text-mint-dark-11',
          prizeStatus === 'honorable' && 'text-gray-dark-11',
          !prizeStatus && 'text-gray-dark-12',
        )}
      />
      <CornerIcon
        class={cn(
          'absolute bottom-0 left-0 -m-0.5 rotate-180',
          prizeStatus === 'grand' && 'text-amber-400',
          prizeStatus === 'prize' && 'text-mint-dark-11',
          prizeStatus === 'honorable' && 'text-gray-dark-11',
          !prizeStatus && 'text-gray-dark-12',
        )}
      />
      <div class="gap-x-lg flex flex-row">
        <div class="bg-gray-dark-2 h-[88px] w-[88px]">
          {#if browser}
            <ImageRender
              src={project.image?.url ?? ''}
              seed={project.id.toString()}
              class="h-[88px] w-[88px]"
              imgClass="rounded"
            />
          {/if}
        </div>
        <div class="flex flex-1 flex-col items-start gap-y-1">
          <div class="flex w-full items-start justify-between">
            <h2>{project.name}</h2>
          </div>
          <div class="text-gray-dark-11 mt-1 flex items-center justify-end gap-x-2">
            <PinIcon />
            <p>{project.country}</p>
          </div>
          {#if project.tracks && project.tracks.length > 0}
            <div class="mt-md flex flex-wrap gap-2.5">
              {#each project.tracks as track}
                <Tag
                  variant={prizeStatus === 'grand'
                    ? 'gold'
                    : prizeStatus === 'prize'
                      ? 'mint'
                      : 'secondary'}
                  size="xs"
                >
                  {track}
                </Tag>
              {/each}
            </div>
          {/if}
        </div>
      </div>
    </div>
    <HackathonProjectActions
      {project}
      {hackathon}
      {user}
      bind:showDeleteProjectModal
      bind:showLeaveTeamModal
    />
  </div>
</div>
<div class="my-9 flex w-full flex-col items-start gap-y-[2.2rem]">
  <div class="w-full">
    <SectionTitle icon={BookIcon} title="Description" />
    <p class="mt-3 whitespace-pre-wrap break-words">
      {@html linkify(project.description)}
    </p>
  </div>

  {#if project.teamMembers && project.teamMembers.length > 0}
    <div class="w-full">
      <SectionTitle icon={UserGroupIcon} title="Team Details" />
      <HackathonProjectMembers {project} />
    </div>
  {/if}
  {#if project.presentationLink || project.twitterHandle || project.repoLink}
    <div class="w-full">
      <SectionTitle icon={LinkTwoIcon} title="LINKS" />
      {#if showLinkConfirmation}
        <Notification
          class="mt-3 hidden w-full sm:block"
          warning
          title="Verify your project links"
          description="Please ensure all your project links are correct and accessible. You will not be able to edit these links after the hackathon ends."
          dismissAbleStorageKey="project-links-notification-dismissed"
        >
          <ul class="text-gray-dark-12 ml-8 space-y-2 text-sm">
            {#if project.presentationLink}
              <li class="flex items-center">
                <PresentationIcon class="mr-2 h-4 w-4" />
                <span class="font-semibold">Pitch:</span>
                <a
                  href={project.presentationLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-indigo-dark-11 ml-2 hover:underline">{project.presentationLink}</a
                >
              </li>
            {/if}
            {#if project.technicalDemoLink}
              <li class="flex items-center">
                <BuildIcon class="mr-2 h-4 w-4" />
                <span class="font-semibold">Technical Demo:</span>
                <a
                  href={project.technicalDemoLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-indigo-dark-11 ml-2 hover:underline">{project.technicalDemoLink}</a
                >
              </li>
            {/if}
            {#if project.twitterHandle}
              <li class="flex items-center">
                <XLogo class="mr-2 h-4 w-4" />
                <span class="font-semibold">X (Twitter):</span>
                <a
                  href={project.twitterHandle}
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-indigo-dark-11 ml-2 hover:underline">{project.twitterHandle}</a
                >
              </li>
            {/if}
            {#if project.repoLink}
              <li class="flex items-center">
                <FolderGit2Icon class="mr-2 h-4 w-4" />
                <span class="font-semibold">Repository:</span>
                <a
                  href={project.repoLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-indigo-dark-11 ml-2 hover:underline">{project.repoLink}</a
                >
              </li>
            {/if}
          </ul>
        </Notification>
      {/if}
      <div class="mt-3 flex items-center space-x-4">
        {#if project.presentationLink}
          <Tooltip tooltipContent="Pitch">
            <Button quinary size="sm" target="_blank" href={project.presentationLink} icon>
              <PresentationIcon />
            </Button>
          </Tooltip>
        {/if}
        {#if project.technicalDemoLink}
          <Tooltip tooltipContent="Technical Demo">
            <Button quinary size="sm" target="_blank" href={project.technicalDemoLink} icon>
              <BuildIcon class="p-1" />
            </Button>
          </Tooltip>
        {/if}
        {#if project.twitterHandle}
          <Tooltip tooltipContent="X (Twitter)">
            <Button quinary size="sm" target="_blank" href={project.twitterHandle} icon>
              <XLogo />
            </Button>
          </Tooltip>
        {/if}
        {#if project.repoLink}
          <Tooltip tooltipContent="Repository">
            <Button quinary size="sm" target="_blank" href={project.repoLink} icon>
              <FolderGit2Icon />
            </Button>
          </Tooltip>
        {/if}
      </div>
    </div>
  {/if}
  {#if project.additionalInfo && project.additionalInfo.length > 0}
    <div class="w-full">
      <SectionTitle icon={LongPaperIcon} title="Additional Information" />
      <ExpandableText class="mt-3" text={project.additionalInfo} maxHeight={130} />
    </div>
  {/if}
  {#if !isProjectFromCurrentHackathon || hackathon?.isProjectDirectoryEnabled}
    <div class="w-full">
      <SectionTitle icon={DoubleBubbleIcon} title="Comments" />
      <HackathonProjectComments {project} {user} class="mt-8" />
    </div>
  {/if}
</div>

<LeaveTeamModal isOpen={showLeaveTeamModal} onClose={() => (showLeaveTeamModal = false)} />
<DeleteProjectModal
  isOpen={showDeleteProjectModal}
  onClose={() => (showDeleteProjectModal = false)}
/>

<style>
  @keyframes shine {
    0% {
      opacity: 1;
      transform: translate(-100%, -100%) rotate(45deg);
    }
    100% {
      opacity: 1;
      transform: translate(100%, 100%) rotate(45deg);
    }
  }

  .shine-animation {
    position: relative;
    overflow: hidden;
  }

  .shine {
    position: absolute;
    top: -100%;
    left: -100%;
    width: 300%;
    height: 300%;
    opacity: 0;
    animation: shine 8s infinite;
    pointer-events: none;
  }

  .shine-gold {
    background: linear-gradient(
      60deg,
      transparent 45%,
      rgba(242, 196, 79, 0.2) 50%,
      transparent 55%
    );
  }

  .shine-mint {
    background: linear-gradient(
      60deg,
      transparent 45%,
      rgba(148, 246, 223, 0.2) 50%,
      transparent 55%
    );
  }

  .shine-silver {
    background: linear-gradient(
      60deg,
      transparent 45%,
      rgba(255, 255, 255, 0.2) 50%,
      transparent 55%
    );
  }
</style>
